import { G } from '@mobily/ts-belt';
import { Box } from '@mui/material';
import { ReactNode } from 'react';
import { useTranslate } from 'react-admin';
import { useWatch } from 'react-hook-form';

import { ShowSection } from '@sh4res/bo-common/components';

type Kind = 'mailing' | 'billing';

const labelOf = (field: string) => `resources.bo_common.b2b.admins.fields.${field}`;

const isFilled = (value: unknown) => G.isNotNullable(value) && value !== '';

const Row = ({ label, value, testId }: { label: string; value: ReactNode; testId: string }) => (
  <Box
    data-testid={testId}
    sx={{
      display: 'flex',
      gap: 2,
      py: 1,
      borderBottom: '1px solid',
      borderColor: 'divider',
    }}>
    <Box sx={{ width: 240, flexShrink: 0, color: 'text.secondary' }}>{label}</Box>
    <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{value}</Box>
  </Box>
);

/**
 * Last step of the correspondent dialogs: read-only recap of the general details and of the
 * correspondence addresses, before saving.
 */
export const AdminConfirmationStep = ({ testId }: { testId: string }) => {
  const translate = useTranslate();
  const values = useWatch();

  const yesNo = (value: unknown) =>
    translate(`resources.bo_common.b2b.admins.correspondence.${value ? 'yes' : 'no'}`);

  return (
    <>
      <ShowSection leftAligned noCard label="resources.bo_common.b2b.admins.steps.general">
        <Row
          label={translate(labelOf('firstname'))}
          value={values.firstname}
          testId={`${testId}_FirstnameRow`}
        />
        <Row
          label={translate(labelOf('lastname'))}
          value={values.lastname}
          testId={`${testId}_LastnameRow`}
        />
        <Row
          label={translate(labelOf('email'))}
          value={values.email}
          testId={`${testId}_EmailRow`}
        />
      </ShowSection>
      {(['mailing', 'billing'] as const).map((kind: Kind) => {
        const isCorrespondent = Boolean(values[`is_${kind}_correspondent`]);
        const hasAddress = [
          values[`${kind}_street`],
          values[`${kind}_postalcode`],
          values[`${kind}_city`],
          values[`${kind}_country`],
        ].some(isFilled);

        return (
          <ShowSection
            key={kind}
            leftAligned
            noCard
            label={`resources.bo_common.b2b.admins.sections.${kind}_address`}>
            <Row
              label={translate(labelOf(`is_${kind}_correspondent`))}
              value={yesNo(isCorrespondent)}
              testId={`${testId}_Is${kind}CorrespondentRow`}
            />
            {isCorrespondent && kind === 'billing' && (
              <Row
                label={translate(labelOf('billing_channel'))}
                value={
                  isFilled(values.billing_channel)
                    ? translate(
                        `resources.bo_common.b2b.admins.choices.billing_channel.${values.billing_channel}`,
                      )
                    : null
                }
                testId={`${testId}_BillingChannelRow`}
              />
            )}
            {isCorrespondent &&
              (hasAddress ? (
                <>
                  {(['street', 'postalcode', 'city', 'country'] as const).map((field) => (
                    <Row
                      key={field}
                      label={translate(labelOf(`${kind}_${field}`))}
                      value={values[`${kind}_${field}`]}
                      testId={`${testId}_${kind}${field}Row`}
                    />
                  ))}
                  <Row
                    label={translate(labelOf(`is_npai_${kind}`))}
                    value={yesNo(values[`is_npai_${kind}`])}
                    testId={`${testId}_IsNpai${kind}Row`}
                  />
                  {Boolean(values[`is_npai_${kind}`]) && (
                    <Row
                      label={translate(labelOf(`npai_${kind}_at`))}
                      value={values[`npai_${kind}_at`]}
                      testId={`${testId}_Npai${kind}AtRow`}
                    />
                  )}
                </>
              ) : (
                <Row
                  label={translate(labelOf(`${kind}_street`))}
                  value={translate(
                    'resources.bo_common.b2b.admins.correspondence.company_address_used',
                  )}
                  testId={`${testId}_${kind}CompanyAddressRow`}
                />
              ))}
          </ShowSection>
        );
      })}
    </>
  );
};
